/**
 * Wording for the "same human on two leads" flag (YLLW-RADAR-101).
 *
 * Separate from the component so the phrasing — which is the whole product
 * here, since the flag's job is to be believed exactly as much as it deserves
 * — can be tested without a DOM.
 */

/** Swedish nouns for the fields a match agreed on. Falls back to the raw
 *  value for a field this dictionary hasn't caught up with yet, so an
 *  unmapped key still shows something. */
const FIELD_LABEL: Record<string, string> = {
  name: 'namn',
  phone: 'telefon',
  email: 'e-post',
  'email domain': 'e-postdomän',
}

/** "namn", "namn och telefon", "namn, telefon och e-post". */
export function matchedFieldsLabel(matchedOn: readonly string[]): string {
  const words = matchedOn.map(f => FIELD_LABEL[f] ?? f)
  if (words.length <= 1) return words.join('')
  return `${words.slice(0, -1).join(', ')} och ${words[words.length - 1]}`
}

/** One warning: a contact on this lead and the other leads' contacts that
 *  matched it on the same fields (YCRM-87). */
export interface DuplicateGroup<M> {
  key: string
  ourContact: string
  matchedOn: string[]
  matches: M[]
}

/** Splits matches into one warning per contact on this lead and per set of
 *  agreeing fields, so a warning never lists a person it does not name.
 *  Keeps the API's order, which puts corroborated matches first. */
export function groupDuplicates<
  M extends { ourContactId: string; ourContact: string; matchedOn: string[] },
>(matches: readonly M[]): DuplicateGroup<M>[] {
  const groups = new Map<string, DuplicateGroup<M>>()
  for (const m of matches) {
    const key = `${m.ourContactId}|${m.matchedOn.join(',')}`
    const group = groups.get(key)
    if (group) group.matches.push(m)
    else groups.set(key, { key, ourContact: m.ourContact, matchedOn: m.matchedOn, matches: [m] })
  }
  return [...groups.values()]
}

/** The one-line verdict for a group: whose details matched, on which
 *  fields, and on how many other leads. */
export function duplicateHeadline(
  ourContact: string,
  matchedOn: readonly string[],
  leadCount: number,
): string {
  const where =
    leadCount === 1 ? 'en kontakt på 1 annan lead' : `kontakter på ${leadCount} andra leads`
  return `${ourContact} har samma ${matchedFieldsLabel(matchedOn)} som ${where}`
}
